import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import { InsuranceCaseData, InsuranceCaseDocument } from '@shared/types';
import { useEffect } from 'react';
import { OrderByClause, WhereClause } from '../queryBuilder';
import { useLoadState } from '@/hooks/useLoadState';

export const useInsuranceCases = (props?: {
  where?: WhereClause<InsuranceCaseData>[];
  orderBy?: OrderByClause<InsuranceCaseData>[];
  enabled?: boolean;
}) => {
  const authSession = useAuthenticatedSession();

  const { loadState, setLoadState } = useLoadState<InsuranceCaseDocument[]>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    if (props?.enabled === false) {
      return;
    }

    return authSession.db.subscribeToInsuranceCasesChanges(
      (snapshot) => {
        setLoadState({
          status: 'loaded',
          data: snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        });
      },
      props?.where ?? [],
      props?.orderBy ?? [],
    );
  }, [authSession, JSON.stringify(props)]);
  return loadState;
};

export const useInsuranceCasesForMatter = (matterId: string | undefined) => {
  return useInsuranceCases({
    where: [['matterId', '==', matterId]],
    enabled: !!matterId,
  });
};

export const useInsuranceCase = (internalCaseId: string | undefined) => {
  const authSession = useAuthenticatedSession();

  const { loadState, setLoadState } = useLoadState<InsuranceCaseDocument>({
    status: 'loading',
  });
  useEffect(() => {
    setLoadState({ status: 'loading' });
    if (!authSession) {
      return;
    }
    if (!internalCaseId) {
      return;
    }

    return authSession.db.subscribeToInsuranceCaseChanges(
      internalCaseId,
      (snapshot) => {
        const data = snapshot.data();
        if (!data) {
          setLoadState({
            status: 'error',
            error: new Error(
              `Insurance case with ID ${internalCaseId} not found`,
            ),
          });
          return;
        }
        setLoadState({
          status: 'loaded',
          data: { ...data, id: snapshot.id },
        });
      },
    );
  }, [authSession, internalCaseId]);
  return loadState;
};
